import { Link } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Mail, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import InstallAppButton from "@/components/InstallAppButton";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export default function Footer() {
  const { t } = useTranslation();
  const year = new Date().getFullYear();
  const [contactOpen, setContactOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const sendMessage = async () => {
    if (sending) return;
    if (!email || !message) {
      toast({ title: "Missing fields", description: "Please enter your email and message.", variant: "destructive" });
      return;
    }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(email)) {
      toast({ title: "Invalid email", description: "Please enter a valid email address.", variant: "destructive" });
      return;
    }
    setSending(true);
    try {
      const id = crypto.randomUUID();
      const { error: insertError } = await supabase
        .from("contact_submissions")
        .insert({ id, name: name || "(no name)", email, subject: subject || null, message });
      if (insertError) throw insertError;

      // Notify owner
      await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "contact-form-notification",
          recipientEmail: "panteleimongogos@gmail.com",
          idempotencyKey: `contact-notify-${id}`,
          templateData: { name, fromEmail: email, subject, message },
        },
      });

      // Confirm to sender
      await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "contact-form-confirmation",
          recipientEmail: email,
          idempotencyKey: `contact-confirm-${id}`,
          templateData: { name, subject, message },
        },
      });

      toast({ title: "Message sent", description: "Thanks! We'll get back to you soon." });
      setContactOpen(false);
      setName(""); setEmail(""); setSubject(""); setMessage("");
    } catch (err: any) {
      console.error(err);
      toast({ title: "Couldn't send", description: err?.message || "Please try again later.", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <footer className="border-t border-border/60 mt-12">
      <div className="container py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          <span>© {year} Tennis Log - My Tennis Ledger.</span>
          <span className="hidden md:inline">{t("footer.rights")}</span>
        </div>
        <div className="flex items-center gap-3 flex-wrap justify-center">
          <Link to="/contact" className="hover:text-foreground transition-colors flex items-center gap-1">
            <Mail className="w-3.5 h-3.5" /> {t("footer.contact")}
          </Link>
          <InstallAppButton variant="footer" />
          <Link to="/features" className="hover:text-foreground transition-colors">Features</Link>
          <Link to="/how-it-works" className="hover:text-foreground transition-colors">How it Works</Link>
          <Link to="/help" className="hover:text-foreground transition-colors">{t("footer.help", { defaultValue: "Help" })}</Link>
          <Link to="/faq" className="hover:text-foreground transition-colors">FAQ</Link>
          <Link to="/about" className="hover:text-foreground transition-colors">About</Link>
          <Link to="/blog" className="hover:text-foreground transition-colors">Blog</Link>
          <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
          <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
          <Link to="/refund" className="hover:text-foreground transition-colors">Refund</Link>
          <Link to="/delete-account" className="hover:text-foreground transition-colors">Delete account</Link>
          <span className="text-primary/80">Game. Set. Match. 🎾</span>
        </div>

      </div>

      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("footer.contactTitle")}</DialogTitle>
            <DialogDescription>{t("footer.contactHint")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Your name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane" />
            </div>
            <div>
              <Label className="text-xs">{t("footer.yourEmail")}</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
            </div>
            <div>
              <Label className="text-xs">{t("footer.subject")}</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">{t("footer.message")}</Label>
              <Textarea rows={5} value={message} onChange={(e) => setMessage(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setContactOpen(false)} disabled={sending}>{t("common.cancel")}</Button>
            <Button onClick={sendMessage} disabled={sending} className="bg-gradient-primary text-primary-foreground">
              {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : t("footer.send")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </footer>
  );
}
